import {
  Center,
  HStack,
  IconButton,
  Input,
  Spinner,
  Tag,
  TagLabel,
  VStack,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import type { IOption } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { VirtualList } from "@src/components/ui-kit/VirtualList";
import { FilterOptionButton } from "@src/utils/components/filters/components/filter-option-button";
import type { Filter } from "@src/utils/components/filters/models";
import useIntersectionObserver from "@src/utils/hooks/useIntersectionObserver";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { observer } from "mobx-react-lite";
import { type FunctionComponent, useCallback, useRef, useState } from "react";
import { useDebounce } from "react-use";

type Props = {
  filter: Filter<any> | undefined;
};

export const PaginatedFilter: FunctionComponent<Props> = observer(
  function PaginatedFilter({ filter }) {
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState(
      new PaginationState(`paginated-filter-${filter?.title}`, {
        perPage: filter?.perPage,
      }),
    );
    const parent = useRef<HTMLDivElement>(null);
    const target = useRef<HTMLDivElement>(null);

    const fetchOptions = async (
      page: number,
      perPage: number,
      searchPhrase: string,
    ) => {
      setLoading(true);

      const data = await filter?.optionsQuery?.({
        page: page,
        first: perPage,
        filters: {
          search: searchPhrase,
        },
      });

      setLoading(false);

      if (!data || !filter) return;

      filter.setOptions(
        page === 1 ? data.options : [...filter.options, ...data.options],
      );

      setPagination((prev) => {
        if (data?.paginatorInfo) {
          prev.setFromPaginatorInfo(data.paginatorInfo);
        }
        return prev;
      });
    };

    useDebounce(
      () => {
        setPagination((prev) => {
          prev.resetPage();
          return prev;
        });
        filter?.setOptions([]);
        fetchOptions(1, pagination.perPage, search);
      },
      300,
      [search],
    );

    useIntersectionObserver({
      root: parent.current,
      target: target,
      onIntersect: () => {
        if (loading) return;
        if (!pagination.hasNextPage) return;
        setPagination((prev) => {
          prev.inc();
          fetchOptions(prev.currentPage, prev.perPage, search);
          return prev;
        });
      },
    });

    const handleSelect = useCallback(
      (option: IOption) => {
        if (!filter) return;
        if (filter.value.includes(option.value)) return;
        filter.rememberOptionLabels([option]);
        filter.setValue([...filter.value, option.value]);
      },
      [filter],
    );

    const handleDeselect = useCallback(
      (value: string) => {
        if (!filter) return;
        filter.setValue(filter.value.filter((val) => val !== value));
      },
      [filter?.value],
    );

    return (
      <VStack alignItems="start" spacing="2">
        <Input
          autoFocus
          onChange={({ target }) => {
            setSearch(target.value);
          }}
          placeholder={filter?.title}
          value={search}
        />
        {(filter?.selectedOptions.length ?? 0) > 0 && (
          <HStack wrap="wrap" overflowY="auto" maxH="100px" spacing="2">
            {filter?.selectedOptions.map(({ label, value }) => (
              <Tag key={`selected-val-${value}`}>
                <TagLabel>{label}</TagLabel>
                <IconButton
                  ml="1"
                  aria-label={t`Deselect filter`}
                  colorScheme="grey"
                  icon={<Icon name="x-close" />}
                  onClick={() => {
                    handleDeselect(value);
                  }}
                  size="xs"
                  variant="ghost"
                />
              </Tag>
            ))}
          </HStack>
        )}
        <VirtualList<IOption>
          estimatedSize={32}
          wrapperProps={{ maxH: "220px", minH: "220px" }}
          items={filter?.notSelectedOptions ?? []}
          itemRenderer={(option, key, style) => (
            <FilterOptionButton
              key={key}
              justifyContent="flex-start"
              w="full"
              minH="32px"
              maxH="32px"
              fontWeight="normal"
              label={option.label}
              onClick={() => {
                handleSelect(option);
              }}
              size="sm"
              style={style}
              variant="ghost"
            />
          )}
          extraBottomContent={
            pagination.hasNextPage ? (
              <Center ref={target} minH="50px">
                <Spinner />
              </Center>
            ) : undefined
          }
        />
      </VStack>
    );
  },
);
